/**
 * ReadinessCatalogs.jsx — Master Data admin for skill_catalog + certification_catalog.
 * Super Admin / Platform Admin only. Fully configurable — G.5 Master Data instruction.
 */
import { useEffect, useState } from "react";
import { Plus, Save, Award, CheckCircle2, ToggleRight, ToggleLeft } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { PageHeader, Card, Badge } from "@/components/primitives";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const CATEGORIES = [
  "front_office", "housekeeping", "engineering", "food_beverage",
  "pool", "safety", "language", "systems", "other",
];

export default function ReadinessCatalogs() {
  const [tab, setTab] = useState("skills");
  return (
    <div className="space-y-6" data-testid="readiness-catalogs-page">
      <PageHeader
        title="Workforce Readiness — Master Data"
        subtitle="Skill and certification catalogs used across Operations."
      />
      <div className="inline-flex bg-hocp-alice/50 rounded-lg p-0.5 border border-hocp-border">
        <button onClick={() => setTab("skills")}
                className={`px-3 py-1.5 rounded-md text-[13px] font-medium ${tab === "skills" ? "bg-white shadow-sm" : "text-hocp-textSecondary"}`}
                data-testid="cat-tab-skills">
          <CheckCircle2 className="w-3.5 h-3.5 inline mr-1"/> Skills
        </button>
        <button onClick={() => setTab("certs")}
                className={`px-3 py-1.5 rounded-md text-[13px] font-medium ${tab === "certs" ? "bg-white shadow-sm" : "text-hocp-textSecondary"}`}
                data-testid="cat-tab-certs">
          <Award className="w-3.5 h-3.5 inline mr-1"/> Certifications
        </button>
      </div>
      {tab === "skills" ? <SkillCatalog/> : <CertCatalog/>}
    </div>
  );
}

function SkillCatalog() {
  const [rows, setRows] = useState([]);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ code: "", label: "", category: "other" });

  const load = async () => {
    try { const { data } = await api.get("/readiness/skills/catalog?include_inactive=true"); setRows(data); }
    catch (e) { toast.error(formatApiError(e) || "Failed to load"); }
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!form.code || !form.label) return toast.error("Code and label required");
    try {
      await api.post("/readiness/skills/catalog", form);
      toast.success("Skill added"); setAdding(false); setForm({ code: "", label: "", category: "other" }); load();
    } catch (e) { toast.error(formatApiError(e) || "Failed"); }
  };
  const toggle = async (row) => {
    try { await api.patch(`/readiness/skills/catalog/${row.code}`, { active: !row.active }); load(); }
    catch (e) { toast.error(formatApiError(e) || "Failed"); }
  };

  return (
    <Card padded testid="skill-catalog-card">
      <div className="flex items-center justify-between mb-4">
        <div className="text-[15px] font-display font-semibold">Skill Catalog · {rows.length} entries</div>
        {!adding && (
          <Button size="sm" variant="outline" onClick={() => setAdding(true)} data-testid="skill-add">
            <Plus className="w-3.5 h-3.5 mr-1"/> Add skill
          </Button>
        )}
      </div>
      {adding && (
        <div className="grid grid-cols-4 gap-2 mb-4 p-3 rounded-lg bg-hocp-alice/30">
          <div><label className="block text-[11px] uppercase tracking-wider text-hocp-textTertiary mb-1">Code</label>
            <Input placeholder="e.g. FIRST_AID" value={form.code}
                   onChange={(e) => setForm(f => ({ ...f, code: e.target.value.toUpperCase() }))}
                   data-testid="skill-code-input"/></div>
          <div><label className="block text-[11px] uppercase tracking-wider text-hocp-textTertiary mb-1">Label</label>
            <Input placeholder="e.g. First Aid" value={form.label}
                   onChange={(e) => setForm(f => ({ ...f, label: e.target.value }))}
                   data-testid="skill-label-input"/></div>
          <div><label className="block text-[11px] uppercase tracking-wider text-hocp-textTertiary mb-1">Category</label>
            <Select value={form.category} onValueChange={(v) => setForm(f => ({ ...f, category: v }))}>
              <SelectTrigger data-testid="skill-category" aria-label="Category"><SelectValue/></SelectTrigger>
              <SelectContent>
                {CATEGORIES.map(c => <SelectItem key={c} value={c}>{c.replace(/_/g, " ")}</SelectItem>)}
              </SelectContent>
            </Select></div>
          <div className="flex gap-2 justify-end items-end">
            <Button variant="outline" size="sm" onClick={() => setAdding(false)}>Cancel</Button>
            <Button size="sm" onClick={create} data-testid="skill-save">
              <Save className="w-3.5 h-3.5 mr-1"/> Save
            </Button>
          </div>
        </div>
      )}
      <div className="overflow-x-auto">
        <table className="w-full text-[13px]">
          <thead className="bg-hocp-alice/40">
            <tr>
              <th className="px-3 py-2 text-left text-[11px] uppercase tracking-wider text-hocp-textTertiary">Code</th>
              <th className="px-3 py-2 text-left text-[11px] uppercase tracking-wider text-hocp-textTertiary">Label</th>
              <th className="px-3 py-2 text-left text-[11px] uppercase tracking-wider text-hocp-textTertiary">Category</th>
              <th className="px-3 py-2 text-center text-[11px] uppercase tracking-wider text-hocp-textTertiary">Active</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(r => (
              <tr key={r.code} className="border-t border-hocp-border" data-testid={`skill-row-${r.code}`}>
                <td className="px-3 py-2 font-mono text-[12px]">{r.code}</td>
                <td className="px-3 py-2">{r.label}</td>
                <td className="px-3 py-2 text-hocp-textSecondary">{r.category.replace(/_/g, " ")}</td>
                <td className="px-3 py-2 text-center">
                  <button onClick={() => toggle(r)} data-testid={`skill-toggle-${r.code}`}>
                    {r.active
                      ? <ToggleRight className="w-5 h-5 text-emerald-600 inline"/>
                      : <ToggleLeft className="w-5 h-5 text-hocp-textTertiary inline"/>}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

function CertCatalog() {
  const [rows, setRows] = useState([]);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ code: "", label: "", default_validity_months: 12, regulatory: false });

  const load = async () => {
    try { const { data } = await api.get("/readiness/certifications/catalog?include_inactive=true"); setRows(data); }
    catch (e) { toast.error(formatApiError(e) || "Failed to load"); }
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!form.code || !form.label) return toast.error("Code and label required");
    try {
      await api.post("/readiness/certifications/catalog", {
        ...form,
        default_validity_months: form.default_validity_months
          ? parseInt(form.default_validity_months) : null,
      });
      toast.success("Certification added"); setAdding(false);
      setForm({ code: "", label: "", default_validity_months: 12, regulatory: false }); load();
    } catch (e) { toast.error(formatApiError(e) || "Failed"); }
  };
  const toggle = async (row) => {
    try { await api.patch(`/readiness/certifications/catalog/${row.code}`, { active: !row.active }); load(); }
    catch (e) { toast.error(formatApiError(e) || "Failed"); }
  };
  const toggleReg = async (row) => {
    try { await api.patch(`/readiness/certifications/catalog/${row.code}`, { regulatory: !row.regulatory }); load(); }
    catch (e) { toast.error(formatApiError(e) || "Failed"); }
  };

  return (
    <Card padded testid="cert-catalog-card">
      <div className="flex items-center justify-between mb-4">
        <div className="text-[15px] font-display font-semibold">Certification Catalog · {rows.length} entries</div>
        {!adding && (
          <Button size="sm" variant="outline" onClick={() => setAdding(true)} data-testid="cert-add">
            <Plus className="w-3.5 h-3.5 mr-1"/> Add certification
          </Button>
        )}
      </div>
      {adding && (
        <div className="grid grid-cols-4 gap-2 mb-4 p-3 rounded-lg bg-hocp-alice/30">
          <div><label className="block text-[11px] uppercase tracking-wider text-hocp-textTertiary mb-1">Code</label>
            <Input placeholder="e.g. FSSAI" value={form.code}
                   onChange={(e) => setForm(f => ({ ...f, code: e.target.value.toUpperCase() }))}
                   data-testid="cert-code-input"/></div>
          <div><label className="block text-[11px] uppercase tracking-wider text-hocp-textTertiary mb-1">Label</label>
            <Input placeholder="e.g. Food Safety License" value={form.label}
                   onChange={(e) => setForm(f => ({ ...f, label: e.target.value }))}
                   data-testid="cert-label-input"/></div>
          <div><label className="block text-[11px] uppercase tracking-wider text-hocp-textTertiary mb-1">Validity (months)</label>
            <Input type="number" placeholder="Blank = no expiry"
                   value={form.default_validity_months ?? ""}
                   onChange={(e) => setForm(f => ({ ...f, default_validity_months: e.target.value }))}
                   data-testid="cert-months-input"/></div>
          <div className="flex items-center gap-2 pt-5">
            <label className="text-[12px]">
              <input type="checkbox" checked={form.regulatory}
                     onChange={(e) => setForm(f => ({ ...f, regulatory: e.target.checked }))}
                     data-testid="cert-regulatory-input"/>
              <span className="ml-1">Regulatory</span>
            </label>
            <Button size="sm" onClick={create} data-testid="cert-save">Save</Button>
            <Button size="sm" variant="outline" onClick={() => setAdding(false)}>Cancel</Button>
          </div>
        </div>
      )}
      <div className="overflow-x-auto">
        <table className="w-full text-[13px]">
          <thead className="bg-hocp-alice/40">
            <tr>
              <th className="px-3 py-2 text-left text-[11px] uppercase tracking-wider text-hocp-textTertiary">Code</th>
              <th className="px-3 py-2 text-left text-[11px] uppercase tracking-wider text-hocp-textTertiary">Label</th>
              <th className="px-3 py-2 text-center text-[11px] uppercase tracking-wider text-hocp-textTertiary">Validity (mo)</th>
              <th className="px-3 py-2 text-center text-[11px] uppercase tracking-wider text-hocp-textTertiary">Regulatory</th>
              <th className="px-3 py-2 text-center text-[11px] uppercase tracking-wider text-hocp-textTertiary">Active</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(r => (
              <tr key={r.code} className="border-t border-hocp-border" data-testid={`cert-row-${r.code}`}>
                <td className="px-3 py-2 font-mono text-[12px]">{r.code}</td>
                <td className="px-3 py-2">{r.label}</td>
                <td className="px-3 py-2 text-center tabular-nums">{r.default_validity_months ?? "—"}</td>
                <td className="px-3 py-2 text-center">
                  <button onClick={() => toggleReg(r)} data-testid={`cert-toggle-reg-${r.code}`}>
                    {r.regulatory
                      ? <Badge tone="warning" size="sm">Regulatory</Badge>
                      : <span className="text-hocp-textTertiary text-[12px]">—</span>}
                  </button>
                </td>
                <td className="px-3 py-2 text-center">
                  <button onClick={() => toggle(r)} data-testid={`cert-toggle-${r.code}`}>
                    {r.active
                      ? <ToggleRight className="w-5 h-5 text-emerald-600 inline"/>
                      : <ToggleLeft className="w-5 h-5 text-hocp-textTertiary inline"/>}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}
